import { useEffect, useMemo, useState } from "react";
import { devClubOverrideHeader, getAuthTokenForRequest } from "@/lib/api";

type FilmstripRequest = {
  matchId?: string | null;
  slot?: string | null;
  start: number;
  end: number;
  frames: number;
  enabled?: boolean;
};

/**
 * Load an access-controlled timeline sprite as an object URL.
 *
 * This intentionally uses raw fetch because an image request may need the
 * legacy bearer token and returns a JPEG blob rather than JSON. The request is
 * aborted and its object URL revoked whenever the visible timeline window
 * changes, preventing stale private media from surviving the component.
 */
export function useTimelineFilmstrip({
  matchId,
  slot,
  start,
  end,
  frames,
  enabled = true,
}: FilmstripRequest): string | null {
  const endpoint = useMemo(() => {
    if (!enabled || !matchId || !slot || end <= start) return null;
    const params = new URLSearchParams({
      slot,
      start: (Math.floor(start * 10) / 10).toFixed(1),
      end: (Math.ceil(end * 10) / 10).toFixed(1),
      frames: String(frames),
    });
    return `/api/matches/${encodeURIComponent(matchId)}/filmstrip?${params.toString()}`;
  }, [enabled, end, frames, matchId, slot, start]);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    setUrl(null);
    if (!endpoint) return;

    const controller = new AbortController();
    let objectUrl: string | null = null;
    let active = true;

    const load = async () => {
      try {
        const token = getAuthTokenForRequest();
        const response = await fetch(endpoint, {
          credentials: "include",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...devClubOverrideHeader(),
          },
          signal: controller.signal,
        });
        if (!response.ok || !active) return;
        const blob = await response.blob();
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        // Missing media, generation failure, and aborts all use the neutral
        // timeline placeholder. Playback and trimming remain available.
      }
    };

    void load();
    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [endpoint]);

  return url;
}
